import { forwardRef, type HTMLAttributes } from "react";
import styles from "./styles.module.css";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/shared/components/common/Card";
import type { ReactNode } from "react";
import { cn } from "@/shared/libs/commonUtils";
import Separator from "@/shared/components/common/separator";
import Flex from "@/shared/components/common/Flex";

interface InfoCardProps extends HTMLAttributes<HTMLDivElement> {
  defaultStyle?: 1 | 2 | 3;
  children?: ReactNode;
  header?: {
    title: string;
    description?: string;
    icon?: ReactNode;
    button?: ReactNode;
  };
  footer?: ReactNode;
  customClassName?: {
    card?: string;
    header?: {
      header?: string;
      dataWrapper?: string;
      dataWrapper__Right?: string;
      innerWrapper?: string;
      title?: string;
      description?: string;
      separator?: string;
    };
    content?: string;
    footer?: string;
  };
}

export default forwardRef<HTMLDivElement, InfoCardProps>(function InfoCard(
  {
    defaultStyle = 1,
    children,
    header,
    footer,
    customClassName,
    ...props
  }: InfoCardProps,
  ref,
) {
  const variantClass = styles[`card--style${defaultStyle}`];

  return (
    <Card
      ref={ref}
      {...props}
      className={cn(styles.card, variantClass, customClassName?.card)}
    >
      {header && (
        <CardHeader
          className={cn(styles.card__header, customClassName?.header?.header)}
        >
          <Flex
            className={cn(
              styles.card__header__dataWrapper,
              customClassName?.header?.dataWrapper,
            )}
          >
            <Flex
              className={cn(
                styles.card__header__dataWrapper__right,
                customClassName?.header?.dataWrapper__Right,
              )}
            >
              {header?.icon}
              <Flex
                className={cn(
                  styles.card__header__innerWrapper,
                  customClassName?.header?.innerWrapper,
                )}
              >
                <CardTitle
                  className={cn(
                    styles.card__header__title,
                    customClassName?.header?.title,
                  )}
                >
                  {header.title}
                </CardTitle>
                {header.description && (
                  <CardDescription
                    className={cn(
                      styles.card__header__description,
                      customClassName?.header?.description,
                    )}
                  >
                    {header.description}
                  </CardDescription>
                )}
              </Flex>
            </Flex>
            {header?.button}
          </Flex>
          <Separator
            direction="horizontal"
            className={cn(
              styles.card__separator,
              customClassName?.header?.separator,
            )}
          />
        </CardHeader>
      )}
      <CardContent className={cn(styles.card__body, customClassName?.content)}>
        {children}
      </CardContent>
      {footer && (
        <CardFooter
          className={cn(styles.card__footer, customClassName?.footer)}
        >
          {footer}
        </CardFooter>
      )}
    </Card>
  );
});
